// The questions of the client application that a client can mark "Not
// applicable".
//
// The firm requires an answer to each question of the application, because an
// investment manager must get a form with no blank field. Some questions do
// not apply to each client: a second nationality, a home telephone number, a
// Tax Identification Number "if applicable", the detail of an amount of £0.
// Each such question gets a "Not applicable" tick beside it. The tick
// completes the question, and a staff reader sees "Not applicable".
//
// THIS MODULE IS THE ONE LIST. The version view of a bank that requires every
// answer reads it (`completedQuestion` in `client-application-questions.ts`).
// A question can also carry `notApplicable: true` itself. A rule here matches
// a question id, so it covers each copy of a question in each form, and each
// row of a repeated block (a stakeholder, a member, a party of a trust).

/** A question id that ends with one of these takes a "Not applicable" tick. */
const NOT_APPLICABLE_SUFFIXES: readonly string[] = [
    // An address with no county.
    '_county',
    // A person can have one telephone number of the three.
    'mobile_number',
    'home_number',
    'business_number',
    // A person with one nationality.
    'second_country_of_nationality',
    // An entity with no Legal Entity Identifier.
    '_lei',
    // The share of an amount of £0.
    '_ownership',
];

/** A question id that matches one of these takes a "Not applicable" tick. */
const NOT_APPLICABLE_PATTERNS: readonly RegExp[] = [
    // A country that issues no Tax Identification Number.
    /tax_residency_\d_tin$/,
    // The individual form opens the four rows of dependents together, and a
    // client can have one dependent only.
    /^dependent_[234]_/,
];

/** Each other question that takes a "Not applicable" tick. */
const NOT_APPLICABLE_IDS: ReadonlySet<string> = new Set([
    'tax_identification_number_unavailable_reason',
    'nominated_representative_tax_identifier',
    // A client who has no employer.
    'job_title',
    'employer_name',
    // The detail of an amount that can be £0.
    'income_all_other_sources',
    'commitment_other_significant_expenses_detail',
    'asset_other_detail',
    'liability_other_debt_detail',
    'liability_anticipated_mortgage_payoff_date',
    'company_income_all_other_sources',
    'company_commitment_other_significant_expenses_detail',
    'company_asset_other_detail',
    'company_liability_other_debt_detail',
    // An entity with no telephone number or email address of its own.
    'company_phone_number',
    'company_email',
    'trust_phone_number',
    'trust_email',
    // A new SIPP has no account number yet.
    'sipp_account_number',
    // A bank account in the United Kingdom.
    'bank_iban',
    'bank_bic_aba',
    'bank_account_currency',
    'third_party_company_name',
    // A will that no law firm holds.
    'will_law_firm',
    'will_firm_contact_name',
    'will_firm_contact_details',
    // A client with no restriction, no other experience, no note, or no
    // context to give.
    'investment_restrictions',
    'other_product_experience',
    'nominated_representative_other_product_experience',
    'additional_notes',
    'isa_other_family_members_context',
    'isa_junior_requirements_context',
    // The form lets a client keep a health detail to themselves.
    'other_health_conditions_detail',
    'major_life_event_detail',
    'nominated_representative_other_health_conditions_detail',
    // The form marks the intended risk profile "optional".
    'intended_risk_profile',
]);

/** Whether a client can mark the question "Not applicable". */
export function isClientApplicationNotApplicableQuestion(questionId: string): boolean {
    return (
        NOT_APPLICABLE_IDS.has(questionId) ||
        NOT_APPLICABLE_SUFFIXES.some((suffix) => questionId.endsWith(suffix)) ||
        NOT_APPLICABLE_PATTERNS.some((pattern) => pattern.test(questionId))
    );
}
